/**
 * Dictionnaire FRANÇAIS — référence.
 *
 * Les chaînes sont reprises AU CARACTÈRE PRÈS de ce que les écrans affichaient avant migration,
 * apostrophes typographiques comprises. C'est ce qui permet aux 261 assertions de texte des tests
 * existants de passer sans modification : migrer un écran DÉPLACE son texte, il ne le réécrit pas.
 *
 * Ne portent PAS de traduction, et n'ont donc pas d'entrée ici :
 *   - le corps des trois documents juridiques (CGU, politique, suppression de compte) : la version
 *     française fait foi, et le document publié est généré depuis leur JSX ;
 *   - les MONTANTS payants : ils sont rendus par le SDK RevenueCat dans la devise de l'acheteur,
 *     jamais écrits ici — un montant traduit serait un montant faux. NUANCE apprise le
 *     18/09/2026 : `plans.free.price` EST traduit, et le doit. « Gratuit » n'est pas un
 *     montant mais un mot, et « 0€ » s'affichait en euros à côté de prix rendus en dollars ;
 *   - les noms commerciaux (« Session Focus 30’ ») ;
 *   - les intitulés de sections des PDF exportés : ils suivent la langue du DOCUMENT
 *     (cvs.output_language), jamais celle de l'interface. Les traduire ici serait un bug.
 */
export const fr = {
  common: {
    back: 'Retour',
    scoreSur100: 'sur 100',
    cancel: 'Annuler',
    save: 'Enregistrer',
    retry: 'Réessayer',
    delete: 'Supprimer',
    ok: 'J’ai compris',
  },

  a11y: {
    close: 'Fermer',
    removeLanguage: 'Retirer la langue',
    removeBullet: 'Retirer le point',
    removeProject: 'Retirer le projet',
    removeEducation: 'Retirer la formation',
    removeExperience: 'Retirer l’expérience',
    removeTag: 'Retirer %{tag}',
    useful: 'Utile',
    notUseful: 'Pas utile',
    listen: 'Écouter ce message',
    notifications: 'Notifications',
    settings: 'Paramètres',
    changePhoto: 'Changer la photo de profil',
    changeSearchTarget: 'Changer ton objet de recherche',
    importPhoto: 'Importer une photo',
    score: 'Score : %{score} sur 100',
  },

  nav: {
    carriere: 'Carrière',
    offers: 'Offres',
    news: 'News',
    profile: 'Profil',
    aiAction: 'Action IA',
  },

  // BLOC CONSERVÉ SANS SON ÉCRAN : l'accueil a été supprimé, mais `HomeContentCard` reste — c'est
  // l'affichage du fil de contenu alternjob.com, destiné à l'onglet News. Le préfixe `home` ne
  // désigne donc plus aucun écran : à renommer en `content` le jour où News recevra ce fil, ce qui
  // touchera aussi HomeContentCard et la clé de cache de useContents.
  home: {
    content: {
      seeAll: 'Voir tout',
      new: 'NOUVEAU',
      duration: '%{minutes} min',
      play: 'Écouter',
      // Les huit types de contenu. Les libellés et les accents sont posés ici pour que le
      // branchement de la source n'ait plus qu'à fournir les données : la structure ne rouvrira pas.
      types: {
        podcast: 'Podcast AlternJob',
        tip: 'Le conseil du jour',
        event: 'À ne pas manquer',
        success: 'Ils l’ont fait',
        market: 'Marché et offres',
        masterclass: 'Masterclass',
        challenge: 'Challenge',
        news: 'Nouveau sur Horméia',
      },
      // Contenu FICTIF hérité de la maquette, conservé pour les tests d'affichage.
      sample: {
        title: 'IA et recrutement : ce qui change vraiment en 2024',
        description: 'Conseils, témoignages et tendances du marché de l’emploi.',
      },
    },
  },


  carriere: {
    // NOM de l'écran, distinct de son accroche. Conservé bien que l'écran ne l'affiche plus :
    // `documentLabels.test.ts` et `coverLetterPdf.test.ts` s'en servent comme sonde pour vérifier
    // que la langue d'INTERFACE a bien basculé, indépendamment de la langue des documents.
    title: 'Carrière',
    // Accroche affichée en tête d'écran, en deux morceaux : le second reçoit le dégradé de marque
    // via GradientText, comme le titre de l'onboarding. La coupure est donc porteuse — elle décide
    // quel mot est coloré, et le dégradé se pose sur un BLOC, jamais au milieu d'une ligne.
    //
    // SAUT DE LIGNE EXPLICITE, et non un retour automatique : la coupure dépendrait sinon de la
    // largeur de l'écran, et « que » remonterait sur la première ligne dès qu'il y a un peu plus de
    // place. Un titre d'accroche n'a pas le droit de se recomposer d'un appareil à l'autre.
    headline: 'Deviens le candidat\nque les recruteurs',
    headlineAccent: 'appellent.',
    // Annoncé par VoiceOver après le titre, la description et le libellé du bouton : sans cela,
    // rien ne dit qu'il reste des cartes de part et d'autre de celle qui a le focus.
    position: 'Carte %{index} sur %{total}',
    cv: {
      title: 'CV ATS',
      description:
        'Crée un CV professionnel, optimisé pour passer les filtres et attirer l’attention des recruteurs.',
      cta: 'Analyser mon CV',
    },
    linkedin: {
      title: 'LinkedIn',
      description:
        'Crée un profil professionnel et augmente ta visibilité auprès des recruteurs et entreprises.',
      cta: 'Améliorer mon profil',
    },
    letter: {
      title: 'Lettre de motivation',
      description: 'Rédige des lettres percutantes et personnalisées pour chaque candidature.',
      cta: 'Écrire ma lettre',
    },
    interview: {
      title: 'Entretien IA',
      description: 'Entraîne-toi et réussis tous tes entretiens.',
      cta: 'M’entraîner',
    },
    // Page Carrière sur grand écran : panneau « prochain pas », chiffres et état de chaque outil.
    tableau: {
      prochainPas: 'Ton prochain pas pour décrocher des entretiens',
      premierPas: 'Commence par savoir où tu en es',
      conseilParDefaut: 'Ton CV peut encore progresser : ouvre le détail pour voir les corrections.',
      sansScore: 'Dépose ton CV : tu reçois un score ATS et les corrections qui comptent, en quelques secondes.',
      corriger: 'Corriger mon CV',
      noter: 'Noter mon CV',
      chiffreCv: { one: 'CV enregistré', other: 'CV enregistrés' },
      chiffreLettres: { one: 'lettre écrite', other: 'lettres écrites' },
      chiffreCorrections: { one: 'correction suggérée', other: 'corrections suggérées' },
      statutCvAucun: 'Aucun CV pour l’instant',
      statutCv: { one: '%{count} CV', other: '%{count} CV' },
      statutScore: 'score %{score}',
      statutLettreAucune: 'Aucune lettre pour l’instant',
      statutLettres: { one: '%{count} lettre écrite', other: '%{count} lettres écrites' },
      statutLinkedin: 'Audit en une minute',
      statutInterview: 'Recruteur ou manager',
    },
  },

  offers: {
    title: 'Offres',
    subtitle: 'Bientôt, des offres ciblées selon ton profil et tes recherches.',
    soonTitle: 'Nouvelle fonctionnalité en construction',
    soonBody:
      'On prépare une sélection d’offres d’alternance et de premier emploi qui correspondent à ton CV et à ce que tu cherches. En attendant, ton CV, ton profil LinkedIn et tes lettres se travaillent depuis l’onglet Carrière.',
  },

  // Offres, lot 1 (docs/offres/plan.md). Les CODES (contrats, rythmes, raisons, rubriques) viennent
  // de l'edge function `offres-recherche` ; seuls leurs libellés vivent ici.
  offres: {
    // Affichée hors français seulement (horsFrancais) : en français, c'est évident.
    franceUniquement: 'Les offres concernent uniquement des postes en France et sont rédigées en français.',
    titre: 'Offres',
    sousTitre: 'Choisies selon tes critères',
    credits: { zero: 'Plus de candidature IA ce mois-ci', one: '%{count} candidature IA restante', other: '%{count} candidatures IA restantes' },
    segments: { pourToi: 'Pour toi', sauvegardees: 'Sauvegardées' },
    modifierCriteres: 'Modifier mes critères',
    // « Jobs by Adzuna » : la mention qu'imposent leurs conditions, à l'identique dans les deux langues.
    sources: { france_travail: 'France Travail', lba: 'La bonne alternance', adzuna: 'Jobs by Adzuna' },
    contrats: { alternance: 'Alternance', stage: 'Stage', cdi: 'CDI', cdd: 'CDD', autre: 'Autre contrat' },
    contratsEnLigne: { alternance: 'alternance', stage: 'stage', cdi: 'CDI', cdd: 'CDD', autre: 'autre contrat' },
    // Courts : boutons du formulaire et raisons de la carte. `2j3j` n'est plus un choix, mais une offre
    // peut encore l'annoncer.
    rythmes: {
      '3j2j': '3 j / 2 j',
      '4j1j': '4 j / 1 j',
      '1sem1sem': '1 sem / 1 sem',
      '2sem2sem': '2 sem / 2 sem',
      '3sem1sem': '3 sem / 1 sem',
      '2j3j': '2 j / 3 j',
    },
    rythmesComplets: {
      '3j2j': '3 jours en entreprise, 2 jours à l’école',
      '4j1j': '4 jours en entreprise, 1 jour à l’école',
      '1sem1sem': '1 semaine en entreprise, 1 semaine à l’école',
      '2sem2sem': '2 semaines en entreprise, 2 semaines à l’école',
      '3sem1sem': '3 semaines en entreprise, 1 semaine à l’école',
    },
    dureesStage: { '2': '2 mois', '3': '3 mois', '4': '4 mois', '5': '5 mois', '6': '6 mois' },
    dureesCdd: { lt6: 'Moins de 6 mois', '6a12': '6 à 12 mois', '12plus': '1 an et plus' },
    rayonKm: '%{km} km',
    preferences: {
      titre: 'Tes critères',
      sousTitre: 'On cherche selon ce que tu choisis ici.',
      contratMulti: 'plusieurs choix possibles',
      peuImporte: 'Peu importe',
      cdiSeul: 'Pour un CDI, il n’y a rien d’autre à choisir.',
      verifier: 'Vérifie les champs signalés plus haut.',
      metier: 'Métier visé',
      metierPlaceholder: 'Ex. : chargé·e de communication digitale',
      contrat: 'Type de contrat',
      ville: 'Ville',
      villePlaceholder: 'Ex. : Lyon',
      rayon: 'Rayon',
      rythme: 'Rythme d’alternance',
      rythmeAide: 'Entreprise / école',
      dureeStage: 'Durée du stage',
      dureeCdd: 'Durée du CDD',
      dureeAide: 'On écarte les offres qui annoncent une autre durée.',
      reponsesTitre: 'Réponses clés, jamais inventées',
      permis: 'Permis B',
      oui: 'Oui',
      non: 'Non',
      aRenseigner: 'À renseigner',
      disponibilite: 'Disponible à partir du',
      disponibilitePlaceholder: 'JJ/MM/AAAA',
      voir: 'Voir mes offres',
      metierRequis: 'Indique le métier que tu vises.',
      villeRequise: 'Indique ta ville.',
      // Vérifiée avant d’enregistrer (services/communeFrance.ts) : %{commune} = « Saint-Martin-de-Londres (34) ».
      villeIntrouvable: 'Ville introuvable en France. Vérifie l’orthographe ou choisis une ville française.',
      villeRetenue: 'Ville comprise : %{commune}. Si ce n’est pas la bonne, corrige-la ; sinon, valide à nouveau.',
      contratRequis: 'Choisis au moins un type de contrat.',
      disponibiliteInvalide: 'Écris la date au format JJ/MM/AAAA.',
      erreurEnregistrement: 'Impossible d’enregistrer tes critères. Réessaie.',
      chargement: 'Chargement de tes critères…',
      retour: 'Revenir aux offres',
    },
    raisons: {
      contrat: 'Contrat : %{valeur}',
      contrat_different: 'Contrat différent de ta recherche',
      distance: 'À %{valeur} km de ta ville',
      distance_loin: 'À %{valeur} km, au-delà de ton rayon',
      distance_inconnue: 'Lieu de travail non précisé',
      distance_rayon: 'Dans ton rayon de %{valeur} km',
      rythme: 'Rythme %{valeur}, comme tu le souhaites',
      rythme_different: 'Rythme %{valeur}, différent du tien',
      rythme_absent: 'Rythme d’alternance non précisé',
      metier: 'Intitulé proche de ton métier : « %{valeur} »',
      metier_partiel: 'Intitulé en partie proche : « %{valeur} »',
      metier_rome: 'Même famille de métiers que le tien (code ROME %{valeur})',
      metier_eloigne: 'Intitulé éloigné du métier que tu vises',
      metier_sans_citation: 'Correspond au métier que tu vises',
      metier_partiel_sans_citation: 'Proche du métier que tu vises',
      permis_a_renseigner: 'Permis B demandé : à renseigner',
      permis_manquant: 'Permis B demandé',
    },
    carte: {
      pourquoi: 'Pourquoi ça matche',
      // Pluriel pour l'arabe, dont la forme change de 1 à 2, de 3 à 10 et au-delà : l'écran passe `count`.
      publiee: { one: '%{source} · publiée il y a %{jours} j', other: '%{source} · publiée il y a %{jours} j' },
      publieeAujourdhui: '%{source} · publiée aujourd’hui',
      voirDetail: 'Voir le détail',
      voirOffreComplete: 'Voir l’offre complète',
      contratDuree: '%{contrat} · %{duree}',
      // Durée lue dans l'offre (« 24 Mois »), réécrite dans la langue de l'interface.
      dureeMois: { one: '%{count} mois', other: '%{count} mois' },
      salaire: 'Salaire : %{valeur}',
      entrepriseInconnue: 'Entreprise non communiquée',
      lieuDistance: '%{lieu} · %{km} km',
      score: 'Score de match : %{score} sur 100',
    },
    actions: {
      passer: 'Passer cette offre',
      sauvegarder: 'Sauvegarder cette offre',
      interesse: 'Cette offre m’intéresse',
      aide: 'Swipe à droite si l’offre t’intéresse, à gauche sinon',
      // Sous les trois boutons de la pile : courts, ils doivent tenir sous un rond de 44 points.
      libelles: { passer: 'Passer', sauvegarder: 'Sauvegarder', interesse: 'Intéressé·e' },
    },
    detail: {
      voirCandidature: 'Voir ma candidature',
      alerteMineur: 'À vérifier : cette offre semble comporter %{motifs}, interdit ou encadré pour les moins de 18 ans.',
      motifsMineur: { nuit: 'du travail de nuit', alcool: 'du service d’alcool', hauteur: 'du travail en hauteur' },
      fermer: 'Fermer le détail',
      monMatch: 'Ton match',
      description: 'Description',
      extrait: 'Extrait de l’annonce',
      extraitAide: 'Adzuna ne transmet que le début de l’annonce. Le texte complet est sur leur site.',
      voirOffreComplete: 'Voir l’offre complète',
      entreprise: 'L’entreprise',
      infos: 'Informations',
      voirOffre: 'Voir l’offre d’origine',
      preparer: 'Préparer ma candidature',
      source: 'Source : %{source}',
      majLe: 'Mise à jour le %{date}',
      licence: 'Réutilisation soumise à la licence France Travail',
      retiree: 'Cette offre a été retirée par le recruteur.',
      logo: 'Logo de %{entreprise}',
      sauvegardee: 'Offre sauvegardée',
      rubriques: {
        nature_contrat: 'Nature du contrat',
        type_contrat: 'Type de contrat',
        duree_travail: 'Durée de travail',
        experience: 'Expérience',
        formations: 'Formation',
        competences: 'Compétences',
        qualites: 'Qualités professionnelles',
        savoirs: 'Outils',
        langues: 'Langues',
        permis: 'Permis',
        salaire: 'Salaire',
        secteur: 'Secteur d’activité',
        qualification: 'Qualification',
        deplacements: 'Déplacements',
        conditions: 'Conditions d’exercice',
        postes: 'Nombre de postes',
        accessible_th: 'Accessible aux travailleurs handicapés',
        contact: 'Contact',
        agence: 'Agence France Travail',
        duree_contrat: 'Durée du contrat',
        debut: 'Début du contrat',
        expiration: 'Offre valable jusqu’au',
        rome: 'Code ROME',
      },
    },
    preparation: {
      parallele: 'EN PARALLÈLE',
      motsClesTitre: 'MOTS-CLÉS REPÉRÉS DANS L’OFFRE',
      motsClesAutres: '+%{count}',
      motsClesA11y: { one: '1 mot-clé repéré : %{liste}', other: '%{count} mots-clés repérés : %{liste}' },
      motsClesCouvertsA11y: '%{couverts} mots-clés sur %{total} couverts par ton CV : %{liste}',
      titre: 'Ton IA prépare ta candidature',
      pret: 'Ta candidature est prête',
      pour: 'Pour %{entreprise} · %{poste}',
      pourPoste: 'Pour %{poste}',
      fermer: 'Fermer la préparation',
      terminer: 'Fermer',
      verifier: 'Vérifier ma candidature',
      reessayer: 'Réessayer',
      retourOffres: 'Revenir aux offres',
      pied: 'Rien ne part sans ta validation',
      progression: 'Préparation de la candidature',
      etapes: { analyse: 'Analyse de l’offre', cv: 'CV adapté à l’offre', lettre: 'Lettre de motivation', controle: 'Contrôle qualité' },
      attente: 'En attente',
      enCours: 'En cours…',
      echecEtape: 'Cette étape a échoué',
      motsCles: { zero: 'aucun mot-clé', one: '1 mot-clé', other: '%{count} mots-clés' },
      questions: { zero: 'aucune question', one: '1 question', other: '%{count} questions' },
      analyseFaite: '%{motsCles}, %{questions} repérés',
      cvFait: { zero: 'Aucun mot-clé intégré', one: '1 mot-clé intégré', other: '%{count} mots-clés intégrés' },
      titreAligne: ', titre aligné sur l’offre',
      lettreFaite: 'Lettre de %{count} mots',
      controleFait: { zero: 'Aucun point à confirmer', one: '1 point à confirmer', other: '%{count} points à confirmer' },
      couverture: 'Mots-clés couverts : %{couverts} sur %{total}',
      erreurs: {
        quota: 'Tu as utilisé toutes tes candidatures IA ce mois-ci.',
        cv_manquant: 'Il te faut un CV principal pour préparer une candidature. Crée ou importe ton CV dans Carrière.',
        offre_retiree: 'Cette offre n’est plus en ligne : la candidature ne peut pas être préparée.',
        profil_trop_eloigne: 'Cette offre est trop éloignée de ton CV pour écrire une lettre crédible.',
        echec: 'Une étape a échoué. Réessaie : les étapes déjà faites sont gardées.',
      },
    },
    avant: {
      fermer: 'Fermer',
      naissanceTitre: 'Ta date de naissance',
      naissanceRaison: 'Elle sert à adapter le ton de ta lettre et à te prévenir si une offre n’est pas permise avant 18 ans. Tu ne la donnes qu’une fois.',
      naissanceFormat: 'JJ/MM/AAAA',
      erreurNaissance: 'Ta date de naissance n’a pas pu être enregistrée. Réessaie.',
      continuer: 'Continuer',
      annuler: 'Annuler',
      eloigneeTitre: 'Cette offre est loin de ton profil',
      eloigneeTexte: 'Ton match est de %{score} sur 100. Tu peux préparer ta candidature quand même : un crédit sera utilisé si elle aboutit.',
      prepareQuandMeme: 'Préparer quand même',
      nePasPreparer: 'Ne pas préparer',
    },
    relecture: {
      titre: 'Vérifie ta candidature',
      fermer: 'Fermer la relecture',
      erreurLecture: 'Ta candidature n’a pas pu être chargée.',
      reessayer: 'Réessayer',
      manquants: 'Absents de ton CV : %{liste}',
      aConfirmer: 'À vérifier avant d’envoyer',
      raisons: {
        nombre: 'Chiffre absent de ton CV',
        date: 'Date absente de ton CV',
        competence: 'Compétence que ton CV ne montre pas',
        a_confirmer: 'À confirmer',
      },
      exact: 'Oui, c’est exact',
      corriger: 'Corriger',
      enregistrer: 'Enregistrer',
      annuler: 'Annuler',
      champCorrection: 'Ta correction',
      confirme: 'Confirmé',
      corrige: 'Corrigé',
      erreurCorrection: 'Cette correction n’a pas pu être enregistrée. Réessaie.',
      onglets: { cv: 'CV', lettre: 'Lettre', reponses: 'Réponses' },
      legende: 'Surligné : modifié par l’IA pour cette offre.',
      formation: 'Formation',
      competences: 'Compétences',
      langues: 'Langues',
      objet: 'Objet :',
      copierLettre: 'Copier la lettre',
      erreurCopie: 'Le partage n’a pas pu s’ouvrir. Réessaie.',
      reponses: {
        oui: 'Oui',
        non: 'Non',
        date: 'Disponible le %{date}',
        age: '%{age} ans',
        aRenseigner: 'À renseigner',
        aucune: 'Aucune question éliminatoire repérée dans cette offre.',
      },
      exporterCv: 'Exporter le CV (PDF)',
      exporterLettre: 'Exporter la lettre (PDF)',
      erreurExport: 'L’export a échoué. Réessaie.',
      voirOffre: 'Voir l’offre',
      voirOffreComplete: 'Voir l’offre complète',
      extrait: 'Préparée à partir d’un extrait de l’annonce : relis l’offre complète avant d’envoyer ta candidature.',
    },
    etats: {
      chargement: 'On cherche tes offres…',
      erreurTitre: 'Impossible de charger les offres',
      erreurTexte: 'Vérifie ta connexion, puis réessaie.',
      reessayer: 'Réessayer',
      videTitre: 'Aucune offre pour ces critères',
      videTexte: 'Élargis ton rayon ou essaie un métier proche.',
      videTexteDuree: 'Élargis ton rayon, essaie un métier proche, ou assouplis la durée du contrat.',
      epuiseTitre: 'Tu as vu toutes les offres du moment',
      epuiseTexte: 'Reviens plus tard, ou élargis tes critères pour en voir d’autres.',
      sourcePartielle: 'Une source d’offres ne répond pas : la liste peut être incomplète.',
      sauvegardeesVideTitre: 'Aucune offre sauvegardée',
      sauvegardeesVideTexte: 'Touche le signet d’une offre pour la retrouver ici.',
      retiree: 'Offre retirée',
      decisionEchec: 'Ton choix n’a pas pu être enregistré. Vérifie ta connexion.',
    },
  },

  // LES ARTICLES EUX-MÊMES RESTENT EN FRANÇAIS quelle que soit la langue de l'interface : titres,
  // extraits et rubriques viennent du CMS d'alternjob.com, qui ne publie qu'en français. Seule
  // l'ossature de l'écran se traduit. Compromis assumé pour ce lot.
  news: {
    title: 'News',
    segments: { forYou: 'Pour toi', favorites: 'Favoris' },
    // Le fil est CHRONOLOGIQUE PUR, dans l'ordre rendu par l'API : aucun tri ni personnalisation
    // côté client. « Pour toi » nomme l'onglet, il ne promet pas un algorithme.
    verified: 'Éditeur vérifié',
    readingTime: '%{count} min de lecture',
    relative: {
      today: 'Aujourd’hui',
      yesterday: 'Hier',
      days: { one: 'Il y a %{count} j', other: 'Il y a %{count} j' },
      weeks: { one: 'Il y a %{count} sem.', other: 'Il y a %{count} sem.' },
      months: { one: 'Il y a %{count} mois', other: 'Il y a %{count} mois' },
      years: { one: 'Il y a %{count} an', other: 'Il y a %{count} ans' },
    },
    // LE LIKE EST UN SIGNAL PUBLIC (« ça intéresse »), le favori une liste de lecture PRIVÉE. Deux
    // gestes, deux tables, deux vocabulaires : « aimer » et « enregistrer » ne doivent pas se
    // confondre à la lecture.
    like: {
      add: 'Aimer cet article',
      remove: 'Ne plus aimer',
      count: {
        zero: 'Aucun like',
        one: '%{count} like',
        other: '%{count} likes',
      },
      failed: 'Impossible de mettre à jour ce like.',
    },
    favorite: {
      add: 'Ajouter aux favoris',
      remove: 'Retirer des favoris',
      added: 'En favori',
      notAdded: 'Pas en favori',
      failed: 'Impossible de mettre à jour ce favori.',
    },
    errorTitle: 'Le fil n’a pas pu être chargé',
    errorBody: 'Vérifie ta connexion, puis réessaie.',
    emptyTitle: 'Aucun article pour l’instant',
    emptyBody: 'Les nouveaux articles apparaîtront ici dès leur publication.',
    favoritesEmptyTitle: 'Aucun favori',
    favoritesEmptyBody: 'Touche le signet d’un article pour le retrouver ici.',
    favoritesSignedOutTitle: 'Connecte-toi pour garder tes articles',
    favoritesSignedOutBody: 'Tes favoris sont rattachés à ton compte et te suivent d’un appareil à l’autre.',
    end: 'Tu es à jour.',
  },

  notifications: {
    welcome: {
      title: 'Bienvenue sur Horméia 👋',
      body: 'Commence par générer un CV optimisé ATS, puis entraîne-toi à l’entretien avec l’IA. Deux étapes, et tu décroches plus d’entretiens.',
    },
    quantify: {
      title: 'Chiffre tes résultats',
      body: 'Sur ton CV, remplace « gestion des réseaux sociaux » par « +35 % d’engagement en 3 mois ». Les recruteurs et les filtres ATS retiennent les chiffres.',
    },
    followUp: {
      title: 'Relance après 7 jours',
      body: 'Sans réponse une semaine après ta candidature, envoie un message court et poli au recruteur : une relance bien faite double souvent les chances de réponse.',
    },
  },

  myCv: {
    title: 'Mon CV',
    scoreLabel: 'Ton score ATS',
    scoreNone: 'Lance une première notation pour découvrir ton score ATS.',
    scoreClean: 'Aucune amélioration en attente. Ton CV est prêt.',
    // i18n-js choisit la forme sur `count` : le pluriel n'est plus une concaténation ternaire.
    scoreAdvice: {
      one: '%{count} amélioration peut encore augmenter tes chances.',
      other: '%{count} améliorations peuvent encore augmenter tes chances.',
    },
    scoreActionFirst: 'Noter mon CV',
    scoreActionDetail: 'Voir le détail',
    createOrImport: 'Créer ou importer',
    importTitle: 'Importer mon CV',
    importSubtitle: 'PDF ou Word',
    createTitle: 'Créer un CV',
    createSubtitle: 'À partir d’un modèle',
    improve: 'Améliorer mon CV',
    scoreRowTitle: 'Note mon CV',
    scoreRowSubtitle: 'Score ATS et priorités',
    optimiseTitle: 'Optimise mon CV',
    optimiseSubtitle: 'Améliore l’existant',
    myCvs: 'Mes CV',
    savedCount: {
      zero: 'Aucun CV enregistré',
      one: '%{count} CV enregistré',
      other: '%{count} CV enregistrés',
    },
    // Nombre encore inconnu (chargement, lecture ratée) : jamais « Aucun CV enregistré ».
    savedListOpen: 'Voir mes CV enregistrés',
    listSubtitle: 'Retrouve, rouvre et gère tes CV',
    priorityLabel: 'À corriger en premier',
    importShort: 'Importer',
    createShort: 'Créer un CV',
    cvNote: 'CV noté',
    changer: 'Changer',
  },

  cvList: {
    title: 'Mes CV',
    subtitle: 'Retrouve, rouvre et gère tes CV enregistrés.',
    loadError: 'Impossible de charger tes CV. Réessaie.',
    emptyTitle: 'Aucun CV pour l’instant.',
    emptyText: 'Crée ton premier CV optimisé ATS en quelques minutes.',
    emptyCta: 'Créer mon CV',
    primary: 'Principal',
    setPrimary: 'Définir comme principal',
    edit: 'Éditer',
    preview: 'Aperçu',
    others: 'Mes autres CV',
    score: 'Score ATS %{score}',
    more: 'Plus d’actions',
    deleteTitle: 'Supprimer ce CV ?',
    deleteBody: '« %{title} » sera définitivement supprimé.',
  },

  cvImprove: {
    title: 'Optimise mon CV',
    subtitle: 'L’IA propose des modifications précises, tu gardes celles que tu veux.',
    loading: 'L’IA analyse ton CV…',
    blocked: 'Ce parcours est réservé aux abonnés.',
    notFound: 'CV introuvable. Reviens à la notation et réessaie.',
    saveFailed: 'Enregistrement impossible.',
    reponseInvalide: 'La réponse est incomplète. Réessaie dans un instant.',
    defaultTitle: 'CV optimisé',
    choixQuestion: 'Souhaites-tu importer une fiche ou description de poste pour améliorer la correspondance de ton CV avec l’offre ?',
    choixDeja: 'Tu as noté ton CV avec une fiche de poste. Veux-tu t’en servir pour l’optimiser ?',
    choixNon: 'Non, optimiser directement',
    choixOui: 'Oui, ajouter une fiche de poste',
    choixUtiliser: 'Utiliser la fiche déjà importée',
    ficheTitre: 'Fiche de poste',
    ficheSousTitre: 'Ton CV sera adapté à cette offre, sans rien inventer.',
    ficheChamp: 'CAPTURES, PDF OU TEXTE',
    ficheAide: 'Combine-les si besoin : ils seront lus comme une seule offre. 5 captures et 8 Mo au plus, PDF de 20 pages au plus.',
    optimiser: 'Optimiser mon CV',
    passer: 'Passer',
    ficheUtilisee: 'Adapté à la fiche de poste fournie.',
    // Optimisation par modifications (1.2.0).
    revue: {
      titre: {
        one: '%{count} modification proposée',
        other: '%{count} modifications proposées',
      },
      aide: 'Accepte ou refuse chaque modification. Rien ne change dans ton CV tant que tu n’appliques pas.',
      aideTitre: 'Accepte ou refuse chaque modification.',
      aideSuite: 'Rien ne change dans ton CV tant que tu n’appliques pas.',
      // Libellé SEUL, posé à côté du nombre affiché dans sa tuile (le titre complet reste `titre`).
      libelle: { one: 'modification proposée', other: 'modifications proposées' },
      score: 'Le CV modifié n’est pas re-noté : note-le ensuite pour connaître son score ATS.',
    },
    tags: { impact: 'Impact', action: 'Verbe d’action', keywords: 'Mots-clés', clarity: 'Clarté', title: 'Titre' },
    modif: { avant: 'Avant', apres: 'Après', appliquer: 'Appliquer : %{nom}', titreCv: 'Titre du CV' },
    pied: {
      retenues: 'Retenues : %{count} sur %{total}',
      appliquer: {
        one: 'Appliquer %{count} modification',
        other: 'Appliquer %{count} modifications',
      },
      aucune: 'Aucune modification retenue',
    },
    vide: { titre: 'Ton CV est déjà solide', detail: 'L’IA n’a trouvé aucune modification utile à proposer. Rien n’a été décompté de ton quota.' },
    scoreInconnu: { titre: 'Score non mesuré', detail: 'Note ce CV pour connaître son score ATS.' },
  },

  // Fiche de poste partagée (notation, « Modifier mon CV ») : le choix parmi les offres de l'onglet Offres.
  ficheDePoste: {
    choisirOffre: 'Choisir une offre',
    chargement: 'Chargement de tes offres…',
    erreur: 'Impossible de charger tes offres sauvegardées.',
    vide: 'Aucune offre sauvegardée. Garde une offre dans l’onglet Offres pour la retrouver ici.',
    coller: 'Coller l’annonce complète',
    compteur: '%{n} / %{max} caractères',
    retirerCapture: 'Retirer la capture %{n}',
    retirerFichier: 'Retirer le fichier',
    erreurs: {
      tropDeCaptures: '%{max} captures au maximum.',
      captureIllisible: 'Une capture n’a pas pu être lue. Choisis une autre image.',
      tropLourd: 'Les captures dépassent 8 Mo au total. Retires-en une pour continuer.',
      pdfTropLong: 'Ce PDF dépasse 20 pages. Garde seulement la fiche de poste.',
    },
  },

  cvScore: {
    failed: 'La notation a échoué. Réessaie.',
    title: 'Note ton CV',
    targetRoleLabel: 'Poste visé',
    targetRoleHint: 'Le poste que tu vises : l’IA note aussi l’adéquation de ton CV à ce poste.',
    targetRolePlaceholder: 'Ex. Développeur web en alternance',
    offerLabel: 'Fiche de poste',
    offerHint: 'Ajoute l’offre pour une note de correspondance et des conseils ciblés sur ce poste.',
    offerPlaceholder: 'Colle l’offre ici…',
    offerFile: 'Offre (PDF/Word)',
    offerCaptures: 'Captures',
    optional: 'Optionnel',
    ficheAdded: 'Ajoutée',
    moreAccurate: 'Pour une note plus précise',
    frise: { import: 'Importe', evalue: 'L’IA évalue', score: 'Ton score' },
    dropTitle: 'Dépose ton CV',
    dropSub: 'PDF ou Word. Tu reçois ton score ATS et des conseils concrets.',
    dropReady: 'CV prêt à être noté',
    dropTextReady: 'Le texte de ton CV est prêt.',
    dropPick: 'Choisir un fichier',
    dropChange: 'Changer de fichier',
    hintEmpty: 'Importe un CV pour obtenir ton score ATS.',
    hintReady: 'Prêt : ton score arrive en quelques secondes.',
    submit: 'Noter mon CV',
    atsScore: 'Score ATS',
    offerMatch: 'Correspondance avec l’offre',
    strengths: 'POINTS FORTS POUR CE POSTE',
    gaps: 'ÉCARTS AVEC L’OFFRE',
    saveImported: 'Enregistrer dans Mes CV',
    saveFailed: 'Enregistrement impossible. Réessaie.',
    importedTitle: 'CV importé',
    improve: 'Optimise mon CV',
    verdict: { faible: 'À retravailler', correct: 'Correct, peut mieux faire', bon: 'Bon CV, quelques ajustements', excellent: 'Excellent, prêt à envoyer' },
    resultDetail: {
      zero: 'Aucune amélioration en attente.',
      one: '%{count} amélioration peut encore augmenter tes chances.',
      other: '%{count} améliorations peuvent encore augmenter tes chances.',
    },
    advicePrio: 'À corriger en premier',
    rescore: 'Refaire la note',
    rescoreTitre: 'Refaire la note ?',
    rescoreMessage: 'Tu reviens au formulaire. La note affichée sera perdue.',
    rescoreAnnuler: 'Garder la note',
    criteres: { titre: 'Détail par critère', keywords: 'Mots-clés', layout: 'Mise en page ATS', impact: 'Résultats chiffrés', readability: 'Lisibilité' },
  },

  cvCreate: {
    // Libellés AFFICHÉS. Les libellés envoyés dans le prompt sont distincts et restent français :
    // voir PROMPT_LABELS dans CvCreateScreen.
    questions: {
      role: { label: 'Poste visé', placeholder: 'Ex. Chargé de projet' },
      education: {
        label: 'Formation, diplômes et certifications',
        placeholder: 'Ex. M1 Marketing, IAE Paris',
      },
      experiences: {
        label: 'Expériences professionnelles (stages, alternances, jobs)',
        placeholder: 'Décris tes expériences pro',
      },
      projects: {
        label: 'Projets personnels (perso, associatifs, side-projects)',
        placeholder: 'Décris tes projets perso',
      },
      skills: { label: 'Compétences et outils', placeholder: 'Ex. SQL, SEO, Excel, Figma' },
      languages: { label: 'Langues' },
      interests: {
        label: 'Centres d’intérêt et loisirs',
        placeholder: 'Ex. Trail, photographie, échecs',
      },
      extra: {
        label: 'Quelque chose à ajouter ? (optionnel)',
        placeholder: 'Champ libre, l’IA l’intègrera au bon endroit',
      },
    },
    steps: {
      analyse: 'Analyse de tes réponses',
      format: 'Mise en forme du CV',
      ats: 'Vérification ATS',
    },
  },

  languagePicker: {
    namePlaceholder: 'Langue',
    level: 'Niveau',
    diploma: 'Diplôme (optionnel)',
    diplomaPlaceholder: 'Ex. TOEFL 105 (ou laisse vide)',
    add: 'Ajouter une langue',
  },

  editors: {
    experience: {
      titre: 'Expérience',
      rowTitle: 'Expérience %{n}',
      roleLabel: 'Poste',
      end: 'Fin',
      descriptionLabel: 'Ce que tu as fait',
      descriptionPlaceholder: 'Écris simplement, l’IA le reformule.',
      role: 'Intitulé du poste',
      company: 'Entreprise',
      start: 'Début',
      current: 'Poste actuel',
      description: 'Ce que tu as fait (l’IA le reformulera)',
      add: 'Ajouter une expérience',
    },
    education: {
      titre: 'Formation',
      rowTitle: 'Formation %{n}',
      degree: 'Diplôme / intitulé',
      school: 'Établissement',
      date: 'Date',
      current: 'En cours',
      add: 'Ajouter une formation',
    },
    project: {
      titre: 'Projets',
      rowTitle: 'Projet %{n}',
      descriptionLabel: 'Ce que tu as réalisé',
      descriptionPlaceholder: 'Écris simplement, l’IA le reformule.',
      name: 'Nom du projet',
      date: 'Date',
      current: 'En cours',
      description: 'Ce que tu as réalisé (l’IA le reformulera)',
      add: 'Ajouter un projet',
    },
    bullets: {
      placeholder: 'Décris un résultat / une action',
      add: 'Ajouter un point',
    },
    template: {
      groupLabel: 'Modèle de CV',
      mono: 'Professionnel',
      accent: 'Coloré',
      accentColor: 'Couleur d’accent',
      couleur: {
        lime: 'Lime',
        vert: 'Vert',
        bleu: 'Bleu',
        beige: 'Beige',
        marron: 'Marron',
        ardoise: 'Ardoise',
        marine: 'Marine',
      },
    },
    quantifyHint: 'Pense à chiffrer tes résultats (combien, quel %, quel impact). Ça booste ton score ATS.',
    searchTarget: 'Tu cherches…',
  },

  cvEditor: {
    before: 'AVANT',
    after: 'APRÈS - proposé par l’IA',
    reject: 'Refuser',
    accept: 'Accepter',
    improveWithAi: 'Améliorer avec l’IA',
    // MÊMES MOTS que les sections du PDF, autre axe : ce sont des en-têtes d'ÉDITION, elles
    // suivent l'interface. Celles du document vivent dans utils/documentLabels.
    headline: 'Titre du CV',
    experiences: 'Expériences',
    projects: 'Projets',
    education: 'Formation',
    skills: 'Compétences',
    languages: 'Langues',
    interests: 'Centres d’intérêt',
    headlinePlaceholder: 'Ex. AI Ambassador & Community Coordinator',
    startPlaceholder: 'Ex. Janv. 2024',
    endPlaceholder: 'Ex. En cours',
    projectDatePlaceholder: 'Ex. Mars 2024',
    educationDatePlaceholder: 'Ex. Sept. 2025',
    skillPlaceholder: 'Ex. SQL',
    languagePlaceholder: 'Ex. Anglais - C1',
    interestPlaceholder: 'Ex. Trail',
    experienceLabel: 'Expérience',
    projectLabel: 'Projet',
    startLabel: 'Début',
    endLabel: 'Fin',
    dateLabel: 'Date',
    rewriteRetry: 'Réessaie.',
  },

  qualityCheck: {
    title: 'Ce qui a été amélioré',
    corrections: 'Corrections',
    languagesRemoved: 'Langues retirées (niveau sous B2)',
    skillsNotProven: 'Compétences de l’offre non démontrées',
    toValidate: 'À valider de ton côté',
    removed: 'Éléments supprimés',
    recommendations: 'Recommandations',
  },

  linkedin: {
    sousScores: { titre: 'Détail par section', title: 'Titre', about: 'À propos', experiences: 'Expériences' },
    kit: {
      failed: 'Le kit d’optimisation n’a pas pu être généré. Ton score et tes priorités sont conservés.',
      retry: 'Réessayer le kit',
    },
    title: 'Audit LinkedIn',
    subtitle: 'L’IA lit ton profil et te dit quoi améliorer.',
    verdict: { faible: 'Profil à renforcer', correct: 'Profil correct, peut mieux faire', bon: 'Bon profil, quelques ajustements', excellent: 'Profil excellent' },
    resultDetail: {
      zero: 'Aucune action prioritaire.',
      one: '%{count} action prioritaire pour progresser.',
      other: '%{count} actions prioritaires pour progresser.',
    },
    profilAnalyse: 'Profil analysé',
    relancer: 'Relancer',
    relancerTitre: 'Relancer l’audit ?',
    relancerMessage: 'Tu reviens au formulaire. L’audit et le kit affichés seront perdus.',
    relancerAnnuler: 'Garder l’audit',
    languageLabel: 'Langue de l’audit',
    languageHint: 'Ton profil reste dans sa langue : c’est l’audit et le kit qui sont rédigés dans celle-ci.',
    addCaptures: 'Ajouter des captures',
    analyse: 'Analyser mon profil',
    sourceConnect: 'Compte LinkedIn',
    sourceShots: 'Captures',
    capturesGras: 'Jusqu’à 5 captures',
    capturesSuite: ' de ton profil, dans l’ordre où tu veux qu’on les lise.',
    captureRemove: 'Retirer la capture %{n}',
    profil: { titre: 'Titre', apropos: 'À propos', experiences: 'Expériences' },
    promesses: { audit: 'Un audit détaillé', kit: 'Un kit à copier' },
    hint: { empty: 'Ajoute au moins une capture pour lancer l’audit.', ready: 'Tout est prêt, tu peux lancer l’audit.', connect: 'Connecte ton LinkedIn pour lancer l’audit.', account: 'Ton profil est lu directement depuis ton compte.' },
    missingKeywords: 'Mots-clés à ajouter',
    priorities: 'Actions prioritaires',
    connection: {
      secureTitle: 'Connexion sécurisée',
      secureBody: 'Nous accédons uniquement aux informations nécessaires à l’analyse de ton profil.',
      connected: 'LinkedIn connecté',
      connectedSub: 'Connexion sécurisée, sans ton mot de passe.',
      expired: 'Session LinkedIn expirée. Reconnecte-toi pour la réactiver.',
      unavailable: 'La connexion LinkedIn n’est disponible que dans l’UE, l’EEE et la Suisse.',
      unknown: 'Statut LinkedIn indisponible pour le moment.',
      failed: 'Connexion LinkedIn impossible. Réessaie.',
      fallbackHint: 'Les captures restent utilisables.',
      eligibilityHint:
        'Disponible dans l’UE, l’EEE et la Suisse. Ailleurs, utilise les captures d’écran (onglet Captures) — l’analyse est la même.',
      connect: 'Connecter mon LinkedIn',
      reconnect: 'Reconnecter',
      disconnect: 'Déconnecter',
    },
    optimisation: {
      v1: 'Version 1 (directe recruteur)',
      v2: 'Version 2 (mots-clés)',
      v3: 'Version 3 (différenciante)',
    },
  },

  linkedinKit: {
    ready: 'Ton kit à copier',
    readyHint: 'Touche « Copier » sur un bloc, ou copie tout le kit, puis colle-le dans LinkedIn.',
    copy: 'Copier',
    copied: 'Copié',
    copyFailed: 'Échec',
    copyAll: 'Copier tout le kit',
    kitCopied: 'Kit copié',
    kitCopyFailed: 'Copie impossible : sélectionne le texte à la main.',
    copyLabel: 'Copier : %{nom}',
    headline: 'Titre',
    about: 'Section Infos',
    aboutShort: 'Version courte',
    aboutFull: 'Version complète',
    experiences: 'Expériences clés',
    skills: 'Compétences',
    skillsPinned: 'À épingler (5)',
    skillsExtra: 'Complémentaires',
    search: 'Paramètres de recherche',
    searchTitles: 'Intitulés',
    searchContracts: 'Contrats',
    searchLocations: 'Localisations',
    searchSectors: 'Secteurs',
    searchAvailability: 'Disponibilité',
    express: 'Conseils express',
    photo: 'Photo',
    banner: 'Bannière',
    url: 'URL',
    education: 'Formation (à coller)',
    quality: 'Contrôle qualité',
    keywordsAdded: 'Mots-clés ajoutés',
    languagesRemoved: 'Langues retirées (sous B2)',
    skillsNotProven: 'Compétences non démontrées',
    toValidate: 'À valider',
    removed: 'Éléments supprimés',
    recommendations: 'Recommandations',
    recommended: 'Recommandé',
    experienceN: 'Expérience %{n}',
    recoAsk: 'Recommandation à demander',
    recoAskWho: 'Recommandation à demander (%{who})',
  },

  myLetter: {
    title: 'Ma lettre de motivation',
    subtitle: 'Crée ta lettre de motivation, puis fais-la analyser.',
    pitchLead: 'Une lettre adaptée au poste visé fait toute la différence.',
    arguments: {
      analysed: 'Analysée selon le poste ciblé',
      skills: 'Valorise tes compétences',
      structure: 'Structure convaincante',
      ready: 'Prête à envoyer',
    },
    createSection: 'Créer ma lettre',
    createTitle: 'Créer une lettre',
    createSubtitle: 'À partir d’un de tes CV',
    listSection: 'Mes lettres',
    listCount: 'Mes lettres · %{count}',
    seeAll: 'Tout voir',
    newLetter: 'Nouvelle',
    listSubtitle: 'Retrouve, rouvre et gère tes lettres',
    improveSection: 'Améliorer ma lettre',
    analyseTitle: 'Analyser ma lettre',
    analyseSubtitle: 'Score, erreurs détectées et priorités',
    lastScore: 'Dernier score',
    lastScoreNone: 'Fais analyser une lettre pour découvrir son score.',
    savedCount: {
      zero: 'Aucune lettre enregistrée',
      one: '%{count} lettre enregistrée',
      other: '%{count} lettres enregistrées',
    },
  },

  letterAnalyse: {
    importTitle: 'Importer ma lettre',
    importSubtitle: 'PDF / Word',
    importPicked: 'Fichier importé : %{name}',
    importRemove: 'Retirer le fichier',
    orPaste: 'ou colle son texte',
    // Le texte issu d'un PDF est une TRANSCRIPTION par le modèle, pas l'original : le dire évite
    // que la personne découvre seule un écart avec son fichier.
    rebuiltFromPdf: 'Texte reconstitué depuis le PDF importé.',
    title: 'Analyser ma lettre',
    subtitle: 'L’IA note ta lettre face au poste visé et te dit quoi corriger en priorité.',
    resultTitre: 'Analyse de ma lettre',
    tabSaved: 'Mes lettres',
    tabImport: 'Importer',
    tabPaste: 'Coller',
    importHint: 'PDF ou Word. Elle sera enregistrée dans Mes lettres avec son analyse.',
    pasteLabel: 'Texte de ta lettre',
    pastedTitle: 'Lettre collée du %{date}',
    cvCrossedNone: 'Aucun CV : les faits avancés dans ta lettre ne seront pas vérifiés.',
    offerSub: 'Optionnel, pour une analyse plus précise',
    optionsTitre: 'Plus d’options',
    optionsSousTitre: 'Profil du candidat',
    profilRepris: 'Profil repris de ton compte.',
    sourceSection: 'LA LETTRE À ANALYSER',
    pickSaved: 'Choisir une lettre enregistrée',
    pickSavedEmpty: 'Aucune lettre enregistrée pour l’instant',
    pasteTitle: 'Coller une lettre',
    pasteSubtitle: 'Une lettre écrite ailleurs',
    pastePlaceholder: 'Colle ici le texte de ta lettre…',
    // L'import crée la lettre, comme l'import d'un CV remplit « Mes CV » : une unité de quota
    // dépensée produit un artefact conservé, et son analyse avec.
    pasteSaved: 'Ta lettre collée sera enregistrée dans « Mes lettres » avec son analyse.',
    titleLabel: 'Titre de la lettre (facultatif)',
    titlePlaceholder: 'ex. Alternance chargé de communication',
    offerLabel: 'Annonce',
    offerHint: 'Sans annonce, l’analyse porte sur le métier visé et les attentes du marché.',
    offerPlaceholder: 'Colle l’annonce ici…',
    profileLabel: 'Ton profil',
    profileHint: 'Il détermine le niveau d’exigence : on ne juge pas la lettre d’un collégien comme celle d’un cadre.',
    metierLabel: 'Métier visé',
    metierPlaceholder: 'ex. Chargé de communication',
    submit: 'Analyser ma lettre',
    loading: 'L’IA analyse ta lettre…',
    empty: 'Ajoute la lettre à analyser.',
    failed: 'L’analyse a échoué. Réessaie.',
    // Sans CV, aucune affirmation n'est vérifiable : le dire évite de laisser croire à un contrôle
    // de véracité qui n'a pas eu lieu.
    cvCrossed: 'Analyse croisée avec ton CV principal.',
    cvNotCrossed: 'Aucun CV disponible : les faits avancés dans la lettre n’ont pas été vérifiés.',
    obsolete: 'Cette lettre a été modifiée depuis son analyse. Le score porte sur la version précédente.',
    reanalyse: 'Relancer l’analyse',
    improve: 'Améliorer ma lettre',
    prioritiesSection: 'À corriger en priorité',
    errorsSection: 'Erreurs détectées',
    strengthsSection: 'Ce qui fonctionne déjà',
    detailSection: 'Le détail par critère',
    coveredSection: 'EXIGENCES COUVERTES',
    missingSection: 'EXIGENCES NON ADRESSÉES',
    incoherenceSection: 'Écarts avec ton CV',
    faults: {
      zero: 'Aucune faute relevée',
      one: '%{count} faute relevée',
      other: '%{count} fautes relevées',
    },
    verdicts: {
      a_refaire: 'À refaire',
      a_retravailler: 'À retravailler',
      envoyable_apres_corrections: 'Envoyable après corrections',
      solide: 'Solide',
      excellente: 'Excellente',
    },
    criteres: {
      C1: 'Adéquation au poste',
      C2: 'Preuves et spécificité',
      C3: 'Personnalisation',
      C4: 'Structure et longueur',
      C5: 'Langue et style',
      C6: 'Protocole et forme',
    },
    niveaux: {
      insuffisant: 'Insuffisant',
      fragile: 'Fragile',
      solide: 'Solide',
      excellent: 'Excellent',
    },
    gravites: {
      majeure: 'Majeure',
      moyenne: 'Moyenne',
      mineure: 'Mineure',
    },
  },

  // « Améliorer ma lettre » (1.2.0).
  coverLetterImprove: {
    title: 'Améliorer ma lettre',
    blocked: 'L’amélioration n’a pas pu aboutir.',
    retry: 'Réessayer',
    reponseInvalide: 'La réponse est incomplète. Réessaie dans un instant.',
    saveFailed: 'Enregistrement impossible.',
    copieTitre: '%{titre} (améliorée)',
    tags: { hook: 'Accroche', proof: 'Preuve', keywords: 'Mots-clés', clarity: 'Clarté', closing: 'Conclusion', subject: 'Objet' },
    modif: { objet: 'Objet de la lettre', paragraphe: 'Paragraphe %{numero}', appliquer: 'Appliquer : %{nom}' },
    revue: {
      titre: {
        one: '%{count} modification proposée',
        other: '%{count} modifications proposées',
      },
      aide: 'Accepte ou refuse chaque modification. Ta lettre actuelle ne change pas : tes choix donnent une nouvelle lettre, ajoutée à « Mes lettres ».',
      longueur: 'Corps de la lettre : %{avant} mots avant, %{apres} mots après.',
    },
    pied: {
      retenues: 'Retenues : %{count} sur %{total}',
      appliquer: {
        one: 'Appliquer %{count} modification',
        other: 'Appliquer %{count} modifications',
      },
      aucune: 'Aucune modification retenue',
    },
    vide: { titre: 'Ta lettre est déjà solide', detail: 'L’IA n’a trouvé aucune modification utile à proposer. Rien n’a été décompté de ton quota.' },
    fait: { titre: 'Nouvelle lettre enregistrée', detail: 'Ta lettre améliorée est dans « Mes lettres ». Ta lettre d’origine n’a pas changé.', ouvrir: 'Ouvrir la lettre' },
  },

  letterList: {
    open: 'Ouvrir',
    title: 'Mes lettres',
    subtitle: 'Retrouve, rouvre et gère tes lettres enregistrées.',
    loadError: 'Impossible de charger tes lettres. Réessaie.',
    emptyTitle: 'Aucune lettre pour l’instant.',
    emptyText: 'Génère ta première lettre à partir d’un de tes CV, elle s’enregistrera ici.',
    emptyCta: 'Créer ma lettre',
    deleteTitle: 'Supprimer cette lettre ?',
    deleteBody: '« %{title} » sera définitivement supprimée.',
  },

  letterCreate: {
    erreurFormat: 'La lettre n’a pas pu être rédigée. Réessaie : aucun crédit n’a été utilisé.',
    modifierAnnonce: 'Modifier l’annonce',
    change: 'Changer',
    changeA11y: 'Changer de CV',
    optionsTitre: 'Plus d’options',
    optionsSousTitre: 'Profil, organisation, destinataire, e-mail',
    reprisDuCompte: 'Profil et type de candidature repris de ton compte. Ce que tu choisis ici sera retenu pour la prochaine lettre.',
    failed: 'La lettre n’a pas pu être générée. Réessaie.',
    annonceOr: 'ou importe le fichier de l’annonce',
    annonceFile: 'PDF / Word',
    annonceFilePicked: 'Annonce importée : %{name}',
    annonceFileRemove: 'Retirer le fichier',
    title: 'Générer une lettre',
    subtitle: 'Choisis un CV, colle l’annonce visée et ajuste le ton.',
    resultTitle: 'Ta lettre',
    resultSubtitle: 'Relis-la, vérifie ce qui doit l’être, puis télécharge-la.',
    noCvTitle: 'Il te faut d’abord un CV',
    noCvText:
      'Ta lettre s’appuie sur ton CV pour en tirer tes expériences et tes preuves. Crée ou importe un CV, puis reviens ici.',
    goToCv: 'Aller à Mon CV',
    toConfirm: 'À vérifier avant d’envoyer',
    emailBlock: 'E-mail d’accompagnement',
    download: 'Télécharger ma lettre',
    cvUsed: 'CV utilisé',
    annonce: 'Annonce visée',
    annoncePlaceholder: 'Colle l’annonce ici…',
    profil: 'Profil',
    type: 'Type de candidature',
    ton: 'Ton de la lettre',
    langue: 'Langue de la lettre',
    organisation: 'Organisation visée',
    organisationPlaceholder: 'Ex. ACME',
    metier: 'Métier ciblé',
    metierPlaceholder: 'Ex. Chargé de projet',
    recipientName: 'Destinataire — nom',
    recipientNamePlaceholder: 'Laisse vide si tu ne le connais pas',
    recipientRole: 'Destinataire — fonction',
    recipientRolePlaceholder: 'Ex. Responsable recrutement',
    city: 'Ta ville',
    cityPlaceholder: 'Ex. Lyon',
    addEmail: 'Ajouter un e-mail d’accompagnement',
    generate: 'Générer ma lettre',
    hint: 'Choisis un CV et ajoute l’annonce pour lancer la génération.',
    defaultTitle: 'Lettre de motivation',
    saveFailed: 'Ta lettre est générée mais l’enregistrement a échoué.',
    exportFailed: 'L’export a échoué.',
    wordCount: '%{count} mots',
    profils: {
      collegien: 'Collégien (stage de 3e)',
      lyceen: 'Lycéen',
      etudiant_jeune_diplome: 'Étudiant ou jeune diplômé',
      actif_confirme: 'Actif confirmé',
      manager_cadre_dirigeant: 'Manager ou dirigeant',
      reconversion: 'Reconversion',
    },
    types: {
      reponse_offre: 'Réponse à une offre',
      candidature_spontanee: 'Candidature spontanée',
      mobilite_interne: 'Mobilité interne',
    },
    tons: {
      classique: 'Classique',
      chaleureux: 'Chaleureux',
      direct: 'Direct',
      entrepreneurial: 'Entrepreneurial',
      institutionnel: 'Institutionnel',
      executif: 'Exécutif',
    },
    langues: { francais: 'Français', anglais: 'Anglais' },
  },

  interview: {
    startFailed: 'L’entretien n’a pas pu démarrer. Réessaie.',
    noCv: 'Tu n’as pas encore de CV. Crée d’abord un CV pour lancer un entretien.',
    title: 'Entretien IA',
    cvUsed: 'CV utilisé',
    change: 'Changer',
    changeA11y: 'Changer de CV',
    hintEmpty: 'Indique le poste visé pour lancer l’entretien.',
    loadError: 'Chargement impossible.',
    addCv: 'Ajouter un autre CV',
    posteSection: 'Poste visé',
    posteFile: 'Ajouter une fiche de poste',
    postePaste: 'Coller une offre d’emploi',
    posteExample: 'Ex. : Chargé de marketing',
    pickFile: 'PDF / Word',
    pickCaptures: 'Captures',
    offerPlaceholder: 'Colle ici le texte de l’offre…',
    // Le LIBELLÉ est traduit ; le PRÉFIXE envoyé au prompt reste français (cf. CONTEXT_PREFIX).
    companyType: 'Type d’entreprise (optionnel)',
    companyTypePlaceholder: 'startup, grand groupe, cabinet, PME…',
    adaptNote: 'L’IA analyse et adapte les questions au poste visé.',
    languageLabel: 'Langue de l’entretien',
    start: 'Lancer l’entretien',
    interlocutors: {
      recruiter: {
        label: 'Recruteur',
        points: { motivation: 'Motivation', parcours: 'Parcours', competences: 'Compétences' },
      },
      manager: {
        label: 'Manager',
        points: { terrain: 'Terrain', metiers: 'Métiers', situation: 'Mise en situation' },
      },
    },
    quitTitle: 'Abandonner l’entretien ?',
    quitBody: 'Ta progression sera perdue.',
    quitCancel: 'Continuer l’entretien',
    quitConfirm: 'Abandonner',
    muteVoice: 'Couper la voix du recruteur',
    voixActivee: 'Voix activée',
    downloadPdf: 'Télécharger le PDF',
    pdfErreur: 'Le PDF n’a pas pu être créé. Réessaie.',
    voixCoupee: 'Voix coupée',
    // Limite de vraie voix atteinte : la voix du téléphone prend le relais, le son reste.
    voixReviendra: {
      lundi: 'Voix du téléphone pour l’instant : la voix réaliste reviendra lundi.',
      mois: 'Voix du téléphone pour l’instant : la voix réaliste reviendra le 1er du mois.',
      entretien: 'Voix du téléphone pour la fin de cet entretien : la voix réaliste reviendra au prochain.',
      bientot: 'Voix du téléphone pour l’instant : la voix réaliste reviendra bientôt.',
    },
    // Vraie voix (ElevenLabs) : Free 1 entretien par mois, Plus 2, Pro 5, École 2 par semaine (lundi).
    voixRestante: {
      semaine: 'Voix réaliste : encore %{n} entretiens cette semaine.',
      semaineUn: 'Voix réaliste : encore 1 entretien cette semaine.',
      semaineAucun: 'Voix réaliste : c’est tout pour cette semaine. La voix du téléphone prend le relais jusqu’à lundi.',
      mois: 'Voix réaliste : encore %{n} entretiens ce mois-ci.',
      moisUn: 'Voix réaliste : encore 1 entretien ce mois-ci.',
      moisAucun: 'Voix réaliste : c’est tout pour ce mois-ci. La voix du téléphone prend le relais jusqu’au 1er.',
    },
    unmuteVoice: 'Activer la voix du recruteur',
    listen: 'Écouter',
    finish: 'Terminer l’entretien',
    answerPlaceholder: 'Ta réponse…',
    stopDictation: 'Arrêter la saisie vocale',
    startDictation: 'Répondre à la voix',
    clearAnswer: 'Effacer',
    send: 'Envoyer',
    strengths: 'Points forts',
    improvements: 'À améliorer',
    tips: 'Conseils',
    restart: 'Recommencer',
    phasePresentation: 'Phase présentation',
    phaseOngoing: 'Entretien en cours',
    feedbackHeader: 'Ton feedback',
    overallLabel: 'Appréciation globale',
    dureeMin: '%{n} min',
  },

  profile: {
    fallbackName: 'Ton profil',
    personalInfo: 'Informations personnelles',
    personalInfoSubtitle: 'Gère tes informations et préférences',
    subscription: 'Mon abonnement',
    subscriptionSubtitle: 'Gère ton offre et tes factures',
    activity: 'MON ACTIVITÉ CE MOIS-CI',
    coaching: 'MON COACHING',
    coachingHint: 'L’horaire est convenu par email.',
    bookSession: 'Réserver une séance',
    feedbackTitle: 'Des questions ou des améliorations à nous proposer ?',
    feedbackSubtitle: 'Ton avis compte !',
    avatarSheet: 'Photo ou fond de l’avatar',
    importFailed: 'Import impossible. Réessaie.',
    stats: {
      cvCreated: 'CV créés',
      interviews: 'Entretiens IA',
      cvScores: 'Notations CV',
      linkedinScores: 'Notations LinkedIn',
    },
    noSession: 'Aucune séance programmée',
    sessionFallback: 'Séance',
    lastSession: 'Dernière séance réservée le %{date} — %{formule}',
  },

  personalInfo: {
    firstName: 'Prénom',
    firstNamePlaceholder: 'Ton prénom',
    lastName: 'Nom',
    lastNamePlaceholder: 'Ton nom',
    email: 'Email de contact',
    emailPlaceholder: 'toi@exemple.com',
    phone: 'Téléphone',
    phonePlaceholder: '06 12 34 56 78',
    address: 'Adresse / ville',
    addressPlaceholder: 'Ex. Paris',
    linkedin: 'URL LinkedIn',
    linkedinPlaceholder: 'https://www.linkedin.com/in/…',
  },

  profileCompletion: {
    title: 'Encore une chose',
    birthDatePlaceholder: 'JJ/MM/AAAA',
    continue: 'Continuer',
    later: 'Plus tard',
    saveFailed: 'Enregistrement impossible.',
  },

  notifications_screen: {
    title: 'Notifications',
    empty: 'Pas de nouvelle notification pour le moment.',
    settingsSubtitle: 'Tes rappels et les astuces pour avancer dans ta recherche.',
    tips: 'ASTUCES',
  },

  auth: {
    signup: {
      title: 'Crée ton compte,',
      titleAccent: 'lance-toi.',
      subtitle: 'Améliore ton CV, prépare tes entretiens et avance vers ton contrat.',
      cta: 'Créer mon compte',
    },
    signupEtablissement: {
      title: 'Créez votre compte',
      titleAccent: 'établissement',
      subtitle: 'et donnez à vos étudiants un outil pour préparer leurs candidatures.',
    },
    signupPartenaire: {
      title: 'Créez votre compte',
      titleAccent: 'partenaire',
      subtitle: 'et donnez aux jeunes que vous accompagnez un outil pour préparer leurs candidatures.',
    },
    signin: {
      title: 'Continue à te',
      titleAccent: 'démarquer.',
      subtitle: 'Reprends tes améliorations et avance vers ton prochain entretien.',
      cta: 'Se connecter',
    },
    pending:
      'Si cette adresse n’est pas déjà utilisée, tu vas recevoir un email de confirmation. Pense à vérifier tes spams.',
    invalidEmail: 'Adresse email invalide.',
    invalidPassword: 'Mot de passe invalide.',
    emailLabel: 'E-mail',
    emailPlaceholder: 'prenom@email.fr',
    passwordLabel: 'Mot de passe',
    hidePassword: 'Masquer le mot de passe',
    showPassword: 'Afficher le mot de passe',
    tabSignin: 'Se connecter',
    tabSignup: 'Créer un compte',
    forgot: {
      link: 'Mot de passe oublié ?',
      title: 'Retrouve ton',
      titleAccent: 'accès.',
      subtitle: 'Indique ton adresse e-mail : on t’envoie un lien pour choisir un nouveau mot de passe.',
      cta: 'Envoyer le lien',
      sent: 'Si un compte existe avec cette adresse, un e-mail vient de partir avec un lien pour choisir un nouveau mot de passe. Pense à vérifier tes spams, et ouvre le lien depuis ce téléphone.',
      back: 'Retour à la connexion',
    },
    newPassword: {
      title: 'Choisis ton nouveau',
      titleAccent: 'mot de passe.',
      subtitle: 'Il remplace l’ancien.',
      newLabel: 'Nouveau mot de passe',
      confirmLabel: 'Confirme le mot de passe',
      tooShort: 'Le mot de passe doit faire au moins %{min} caractères.',
      mismatch: 'Les deux mots de passe ne sont pas identiques.',
      cta: 'Enregistrer',
      cancel: 'Annuler et se déconnecter',
    },
    or: 'ou',
    google: 'Continuer avec Google',

    // Erreurs d'authentification. Le tri des codes Supabase vers ces clés vit dans
    // src/utils/authErrors.ts — les trois familles y sont expliquées.
    error: {
      // Famille 1 — l'utilisateur peut agir : dire quoi faire, pas ce qui s'est passé.
      invalidCredentials: 'Email ou mot de passe incorrect.',
      emailNotConfirmed:
        'Ton compte n’est pas encore confirmé. Ouvre le lien qu’on t’a envoyé par email — pense à vérifier tes spams.',
      emailInvalid: 'Cette adresse email n’est pas valide.',
      validationFailed: 'Vérifie ton email et ton mot de passe.',
      weakPassword: 'Mot de passe trop faible. Prends-en un plus long, avec des chiffres et des lettres.',
      rateLimit: 'Trop de tentatives. Réessaie dans quelques minutes.',
      userBanned: 'Ce compte est suspendu. Écris-nous pour en savoir plus.',
      network: 'Connexion impossible. Vérifie ta connexion internet et réessaie.',
      confirmExpired:
        'Ce lien de confirmation n’est plus valable. Recommence la création de ton compte avec la même adresse pour en recevoir un nouveau.',
      confirmWrongDevice: 'Ouvre ce lien depuis le téléphone où tu as créé ton compte.',
      resetExpired: 'Ce lien de réinitialisation n’est plus valable. Demande-en un nouveau avec « Mot de passe oublié ? ».',
      resetWrongDevice: 'Ouvre le dernier e-mail reçu, depuis le téléphone où tu as demandé la réinitialisation.',
      samePassword: 'Choisis un mot de passe différent de l’ancien.',

      // Famille 2 — l'utilisateur ne peut rien : dire que ça vient de nous, et ouvrir la seule
      // porte qui reste. %{providers} dépend de la plateforme (pas de bouton Apple sur Android).
      serverSignup:
        'On n’arrive pas à créer ton compte pour le moment — le problème vient de nous, pas de toi. Réessaie dans quelques minutes, ou continue avec %{providers}.',
      serverSignin:
        'On n’arrive pas à te connecter pour le moment — le problème vient de nous, pas de toi. Réessaie dans quelques minutes, ou continue avec %{providers}.',
      serverOauth:
        'La connexion n’a pas abouti — le problème vient de nous, pas de toi. Réessaie dans quelques minutes, ou connecte-toi avec ton email et ton mot de passe.',
      // Ni « ça vient de nous » ni « réessaie dans quelques minutes » : le blocage vient du
      // navigateur, et réessayer sans rien changer le reproduira à l'identique.
      popupBlocked:
        'Ton navigateur a bloqué la fenêtre de connexion Google. Autorise les fenêtres pop-up pour ce site, puis relance la connexion — ou connecte-toi avec ton email et ton mot de passe.',
      // Le message n'invente PAS une cause : le code n'en porte aucune. Il cesse d'affirmer ce
      // qui est faux (« ça vient de nous », « réessaie ») et pointe les deux réglages
      // vérifiables que la documentation Apple et ses forums citent le plus souvent.
      appleUnknown:
        'La connexion avec Apple n’a pas abouti. Vérifie que tu es connecté à iCloud sur cet appareil et que ton identifiant Apple a la double authentification. Sinon, connecte-toi avec ton email et ton mot de passe.',
      serverSignout: 'La déconnexion n’a pas abouti. Réessaie dans quelques minutes.',
      serverConfirm:
        'On n’arrive pas à confirmer ton compte pour le moment — le problème vient de nous, pas de toi. Réessaie dans quelques minutes, ou continue avec %{providers}.',
      serverReset:
        'On n’arrive pas à réinitialiser ton mot de passe pour le moment — le problème vient de nous, pas de toi. Réessaie dans quelques minutes.',

      // Amont des flux sociaux : l'échec n'a pas encore de code Supabase à ce stade.
      googleStart: 'On n’arrive pas à ouvrir la connexion Google. Réessaie dans quelques minutes.',
      googleResponse: 'La réponse de Google est incomplète. Réessaie.',
      appleResponse: 'La réponse d’Apple est incomplète. Réessaie.',
      deleteFailed:
        'Ton compte n’a pas été supprimé : l’opération a échoué avant toute suppression, tes données sont intactes. Réessaie dans quelques minutes.',
      deleteAppleCancelled: 'Suppression annulée : confirme avec Apple pour supprimer ton compte.',

      providersIos: 'Google ou Apple',
      providersAndroid: 'Google',
    },
  },

  onboarding: {
    highlights: { cv: 'CV', linkedin: 'LinkedIn', interview: 'Entretiens', coach: 'Coach' },
    title: 'Ton profil mérite',
    mots: {
      reponses: 'des réponses.',
      cv: 'un CV qui passe.',
      entretien: 'un entretien.',
      contrat: 'un contrat.',
    },
    sousTitre: 'On t’accompagne du CV jusqu’à la signature de ton contrat.',
    // Six features du schéma. « Offres personnalisées » n'existe pas encore (l'onglet affiche un
    // écran d'attente) : c'est une promesse de PRODUIT sur un écran de présentation, pas un bouton.
    wheel: {
      cv: 'CV ATS',
      linkedin: 'LinkedIn',
      interview: 'Entretiens IA',
      coach: 'Coach expert',
      offers: 'Offres',
      letter: 'Lettre',
      center: 'Ton contrat',
    },
    quickSignup: 'Création rapide',
    dataProtected: 'Données protégées',
    createAccount: 'Créer mon compte',
    haveAccount: 'J’ai déjà un compte',
  },


  acceptTerms: {
    titleOutdated: 'Nos conditions ont changé',
    titleFirst: 'Presque terminé ✨',
    // Découpée en fragments : les liens sont AU MILIEU de la phrase, et l'ordre des mots change
    // d'une langue à l'autre. Une seule chaîne interpolée ne le permettrait pas.
    changedPrefix: 'Nos ',
    changedTermsLink: 'conditions d’utilisation',
    changedMiddle: ' et notre ',
    changedPrivacyLink: 'politique de confidentialité',
    changedSuffix:
      ' ont été mises à jour (version %{version}). Relis-les, puis accepte-les pour continuer à utiliser l’application.',
    firstPrefix: 'Nous avons simplifié nos conditions pour mieux ',
    firstAccent: 'protéger tes données',
    pillars: {
      data: 'Données\nprotégées',
      transparency: 'Transparence\ntotale',
      consent: 'Aucune action\nsans toi',
    },
    docTerms: 'Conditions d’utilisation',
    docPrivacy: 'Politique de confidentialité',
    accept: 'Continuer',
    refuseOutdated: 'Refuser et se déconnecter',
    refuseFirst: 'Plus tard, me déconnecter',
    help: 'Besoin d’aide ?',
    deleteAccount: 'Supprimer mon compte',
    saveFailed: 'Enregistrement impossible. Réessaie.',
  },

  legalHeaders: {
    // Affichée hors français seulement (horsFrancais), près des liens vers les documents juridiques.
    documentsEnFrancais: 'Documents disponibles en français',
    privacy: 'Confidentialité et données',
    terms: 'Conditions d’utilisation',
    dataDeletion: 'Suppression de compte',
  },

  plans: {
    free: {
      tagline: 'Pour démarrer',
      price: 'Gratuit',
      period: 'pour toujours',
      // En tête de carte, avant les candidatures IA : c'est la ligne qui se compare d'une offre à l'autre.
      entretiens: '1 entretien IA / mois, avec la voix naturelle du recruteur',
      features: [
        '1 notation et 1 import de CV / mois',
        '1 audit LinkedIn / mois',
        '1 lettre et 1 analyse de lettre / mois',
        '1 export PDF / mois',
        'Pas de création de CV par IA',
        'Templates de base',
      ],
      // À part : affichées seulement quand l'onglet Offres est actif (individualPlans).
      featuresOffres: ['Offres d’alternance et d’emploi : accès libre', '2 candidatures IA / mois (onglet Offres)'],
    },
    plus: {
      tagline: 'Le bon équilibre',
      period: '/ mois',
      entretiens: '8 entretiens IA / mois',
      voix: '2 entretiens par semaine avec la voix réaliste du recruteur',
      features: [
        '5 créations et 5 imports de CV / mois',
        '5 notations CV / mois',
        '5 audits LinkedIn / mois',
        '5 lettres et 5 analyses de lettre / mois',
        '20 réécritures IA de sections / mois',
        'Export illimité et tous les templates',
      ],
      featuresOffres: ['15 candidatures IA / mois (onglet Offres)'],
    },
    pro: {
      // Plus de « Tout, sans limites » : les candidatures IA restent plafonnées en Pro (décision du 04/10/2026).
      tagline: 'Pour aller au bout de ta recherche',
      period: '/ mois',
      entretiens: '20 entretiens IA / mois',
      voix: '5 entretiens par semaine avec la voix réaliste du recruteur',
      // Sous le prix : l'écart avec Accélérateur en une ligne. Sans l'onglet Offres, sans candidatures.
      ecart: '20 entretiens et 60 candidatures IA par mois',
      ecartSansOffres: '20 entretiens IA par mois',
      features: [
        'Illimité : CV, notations, audits LinkedIn, lettres et analyses',
        'Réécritures IA et exports illimités',
        'Templates premium',
      ],
      featuresOffres: ['60 candidatures IA / mois (onglet Offres)'],
    },
    school: {
      features: [
        'Toutes les fonctionnalités, pour tous les élèves',
        'Idéal écoles et CFA',
        'Facturation par siège',
        'Licence',
        'Tableau de suivi établissement',
        'Bilan trimestriel',
        'Personnalisation',
      ],
    },
  },

  plansExtra: {
    proAnnual: {
      name: 'Pro annuel',
      period: '/an',
      // Remise calculée sur les prix du store (annualOffer.ts), jamais écrite en dur.
      badge: '−%{pourcentage}\u00a0%',
      // « soit X/mois × 12 » : le barré n'est PAS un ancien prix, c'est l'équivalent au tarif
      // mensuel. Sans cette phrase, un prix barré se lit comme une promotion qui n'a jamais existé.
      equivalent: 'soit %{monthly}/mois × 12',
      cta: 'Prendre l’annuel',
    },
  },

  paywall: {
    // Règle 3.1.2 d’Apple, texte validé le 17/10/2026 ; conforme à l’article 7.3 des CGU (le délai de
    // 24 heures est la règle d’Apple, plus stricte que les CGU, sans les contredire).
    mentionAbonnement: 'Abonnement mensuel ou annuel selon l’offre choisie, renouvelé automatiquement pour la même durée, sauf résiliation au moins 24 heures avant la fin de la période en cours. Tu peux résilier à tout moment dans la gestion des abonnements de l’App Store ou de Google Play ; la résiliation prend effet à la fin de la période en cours.',
    tonPlan: 'Ton plan',
    bientotSurPlateforme: 'Les abonnements arrivent bientôt sur cette plateforme.',
    currentPlan: 'Ton plan actuel : ',
    title: 'Passe à la vitesse supérieure',
    // Bandeau affiché UNIQUEMENT quand un appel vient d'être refusé. Les deux cas sont distincts et
    // ne se remplacent pas : dire « ça revient le 1er » à quelqu'un dont la fonctionnalité n'est pas
    // incluse est un mensonge qui se découvre le mois suivant.
    capReached: 'Il ne te reste plus d’utilisations ce mois-ci. Ton compteur repart le %{date}.',
    notIncluded: 'Cette fonctionnalité n’est pas incluse dans ton plan actuel.',
    tabIndividual: 'Individuel',
    tabSchools: 'Écoles',
    restore: 'Restaurer mes achats',
    contact: 'Nous contacter',
    recommended: 'Recommandé',
    subscribe: 'S’abonner',
    pendingApproval: 'Achat en attente d’approbation.',
    prixIndisponibles: 'Prix indisponibles pour le moment. Réessaie dans un instant.',
    genericError: 'Une erreur est survenue.',
    activated: 'Abonnement activé 🎉',
    restored: 'Achats restaurés.',
    activating: 'Achat reçu. Activation de ton abonnement en cours…',
    activationEnCours: 'Achat reçu. Ton abonnement est en cours d’activation : cela peut prendre un instant.',
    restoreEnCours: 'Restauration envoyée. Si un abonnement est lié à ton compte, il apparaîtra dans un instant.',
    schoolMailSubject: 'Offre établissement Horméia',
  },

  coaching: {
    tarifsIndisponibles: 'Tarifs momentanément indisponibles.',
    reessayer: 'Réessayer',
    montantIncoherent:
      'Le paiement est momentanément indisponible : le prix affiché ne correspond pas au montant à débiter. On préfère ne rien prélever tant que ce n’est pas réglé.',
    retourSuccesTitre: 'Paiement confirmé',
    retourSuccesCorps:
      'Ton paiement est encaissé. Tu vas recevoir un email de confirmation avec les détails de ta session.',
    // NI SUCCÈS NI ÉCHEC — le libellé doit le dire sans inquiéter ni promettre.
    retourEnCoursTitre: 'Paiement en cours de traitement',
    retourEnCoursCorps:
      'Ta banque n’a pas encore confirmé le paiement, ce qui est normal avec certains moyens de paiement. Ne recommence pas : tu serais débité deux fois. Tu recevras un email dès que c’est confirmé.',
    retourEchecTitre: 'Paiement refusé',
    retourEchecCorps:
      'Le paiement n’a pas abouti et tu n’as pas été débité. Tu peux réessayer, avec le même moyen de paiement ou un autre.',
    retourAnnuleTitre: 'Paiement annulé',
    retourAnnuleCorps: 'Tu as interrompu le paiement et tu n’as pas été débité. Ton créneau reste disponible.',
    retourVersCoaching: 'Revenir au coaching',
    title: 'Coaching',
    pickSlot: 'Choisir mon créneau',
    soon: 'Bientôt disponible',
    slotBeforePayment: 'Ton créneau est confirmé avant tout paiement.',
    howTitle: 'Comment ça se passe',
    step1: 'Tu choisis ton créneau',
    step2: 'Tu valides ta réservation',
    step3: 'Tu règles ta session',
    pickFormule: 'Choisis ta formule',
    formuleNotFound: 'Formule introuvable. Reviens en arrière et choisis une formule.',
    reserve: 'Réserver',
    checkingSlot: 'On vérifie ton créneau auprès de l’agenda…',
    slotConfirmed: 'Ton créneau est confirmé. Il ne reste qu’à valider le paiement.',
    chosenFormule: 'FORMULE CHOISIE',
    email: 'EMAIL',
    searching: 'CE QUE TU CHERCHES (OPTIONNEL)',
      payUnavailable:
        'Le paiement est momentanément indisponible. Réessaie un peu plus tard.',
    paid: 'Paiement réussi ✓. Ta réservation est confirmée, Julien te recontacte.',
    processing: 'Ton paiement est en cours de traitement. Ne paie pas une seconde fois : tu recevras une confirmation dès qu’il sera validé.',
    securePayment: 'Paiement sécurisé par carte (Stripe). Tu n’es débité qu’une seule fois.',
    nameRequired: 'Indique ton nom.',
    invalidEmail: 'Adresse email invalide.',
    paymentFailed: 'Paiement impossible. Réessaie.',
    paymentFailedShort: 'Paiement impossible.',
  },

  cvCreateFlow: {
    titre: 'Créer un CV',
    etapeSur: '%{n} sur %{total}',
    step1Titre: 'Quel poste vises-tu ?',
    step1Sous: 'L’IA adapte tout le CV à ce poste.',
    step2Titre: 'Ton parcours',
    step2Sous: 'Pas besoin de bien rédiger : l’IA reformule pour toi.',
    step3Titre: 'Pour finir',
    step3Sous: 'Tout est optionnel, tu peux générer ton CV maintenant.',
    nameFromProfile: 'Depuis ton profil',
    importTitre: 'Tu as déjà un CV ?',
    importSous: 'Importe-le, c’est plus rapide.',
    roleRequired: 'Indique le poste visé pour continuer.',
    tuileProjet: 'Un projet',
    tuileAutre: 'Autre chose',
    optionnel: 'Optionnel',
    ajoute: 'Ajouté',
    nameLabel: 'Nom sur le CV',
    namePlaceholder: 'Ex. Jean Dupont',
    languageLabel: 'Langue du CV',
    languageHint: 'Tu peux répondre en français et obtenir un CV en anglais, ou l’inverse.',
    contratLabel: 'Tu cherches…',
    contratHint: 'Facultatif. Touche à nouveau pour retirer.',
    contrats: { alternance: 'Alternance', stage: 'Stage', cdi: 'CDI', cdd: 'CDD' },
    generate: 'Générer mon CV',
    next: 'Continuer',
    buildingTitle: 'On fabrique ton CV.',
    buildingHint: 'Quelques secondes, promis.',
    editTitle: 'Éditer mon CV',
    cancel: 'Annuler',
    save: 'Enregistrer',
    readyTitle: 'Ton CV est prêt',
    atsScore: 'Score ATS',
    edit: 'Éditer',
    saving: 'Enregistrement…',
    retrySave: 'Réessayer la sauvegarde',
    download: 'Exporter mon CV',
    condensePourUnePage: 'Contenu condensé pour tenir sur une page',
    aiAdvice: 'CONSEILS DE L’IA',
    targetOffer: 'CIBLER UNE OFFRE (OPTIONNEL)',
    offerPlaceholder: 'Colle l’offre ici…',
    offerFile: 'Offre (PDF/Word)',
    offerCaptures: 'Captures',
    recommendCta: 'Recommandations pour cette offre',
    recommendSection: 'RECOMMANDATIONS POUR CETTE OFFRE',
    testInScore: 'Tester ce CV dans Note mon CV',
    skillPlaceholder: 'Ex. SQL',
    interestPlaceholder: 'Ex. Trail',
    noterCe: 'Noter ce CV',
    savedIn: 'Enregistré dans Mes CV',
    editsSaved: 'Modifications enregistrées',
    saveFailed: 'Ton CV est généré mais l’enregistrement a échoué.',
    editSaveFailed: 'Enregistrement impossible.',
    offerHint: 'Ajoute une fiche de poste : l’IA te suggère comment valoriser ton parcours pour cette offre (sans rien inventer). À toi d’appliquer via l’édition.',
  },

  help: {
    title: 'Aide et contact',
    faqSection: 'QUESTIONS FRÉQUENTES',
    contactSection: 'NOUS CONTACTER',
    writeUs: 'Écris-nous',
    sendEmail: 'Envoyer un email',
    mailSubject: 'Aide — Horméia',
    faq: {
      ats: {
        q: 'Comment est calculé mon score ATS ?',
        a: 'L’IA analyse ton CV sur les critères que privilégient les logiciels de tri de candidatures (ATS) : présence des mots-clés du poste, structure claire, sections standard et résultats chiffrés. Le score reflète cette compatibilité et s’accompagne de conseils concrets pour l’améliorer.',
      },
      privacy: {
        q: 'Mes données sont-elles confidentielles ?',
        a: 'Tes CV et informations servent à faire fonctionner les fonctionnalités de l’app (notation, création, entretien). Une page dédiée à la confidentialité et à la gestion de tes données sera publiée prochainement ; d’ici là, écris-nous pour toute question.',
      },
      coaching: {
        q: 'Comment fonctionne le coaching ?',
        a: 'Le coaching est un accompagnement humain avec Julien Dauzats, expert RH — distinct des fonctionnalités IA. Tu choisis une formule, tu réserves depuis l’app, et l’échange se fait en visio.',
      },
    },
  },

  misc: {
    personalInfoTitle: 'Infos personnelles',
    save: 'Enregistrer',
    coachNamePlaceholder: 'Ton nom',
    coachGoalPlaceholder: 'Ex. préparer un entretien data la semaine prochaine',
    atsCompatible: 'Compatible ATS',
    completeProfile: 'Complète ton profil',
    completeProfileCta: 'Compléter mon profil',
    later: 'Plus tard',
    feedbackQuestion: 'Ce résultat t’a-t-il été utile ?',
    send: 'Envoyer',
    importPhoto: 'Importer une photo',
    orColour: 'OU UN FOND COLORÉ',
    add: 'Ajouter',
    yearPlaceholder: 'Année',
    comingSoon: 'Bientôt disponible.',
  },

  progress: {
    // ÉTAPE 2 SEULE. Les deux premiers libellés de la séquence d'origine — lecture du profil, grille
    // des 23 erreurs — appartiennent à l'étape 1, qui a déjà rendu son score. Les répéter ici ferait
    // croire que l'analyse recommence.
    linkedinKitSections: [
      'Je rédige ton titre…',
      'Je rédige ta section Infos…',
      'Je reformule tes expériences…',
      'Je choisis tes compétences…',
      'Je règle tes paramètres de recherche…',
      'Je prépare tes conseils express…',
      'Je finalise ton kit…',
    ],

    // SECTIONS RÉELLEMENT PARCOURUES par le prompt d'audit (`LINKEDIN_SYSTEM`), dans l'ordre où il
    // les traite : grille des 23 erreurs, puis titre, Infos, expériences, compétences, recherche,
    // conseils express, contrôle qualité. Au PRÉSENT PROGRESSIF, jamais au passé composé : l'appel
    // Claude est unique et opaque, on décrit ce qui est parcouru, jamais ce qui serait achevé.
     // ÉTAPE 1 de l'audit — score et priorités, ~24 s mesurées. Trois libellés à 8 s (SECTION_MS_AUDIT)
    // posent le terminal à 16 s. Il reste vrai indéfiniment : c'est exactement ce que l'étape produit.
    // Aucun ne nomme une mécanique interne — « la grille des 23 erreurs » est notre vocabulaire, pas
    // celui du candidat.
    linkedinSections: [
      'Je lis ton profil…',
      'Je repère les erreurs classiques…',
      'Je prépare ton score et tes priorités…',
    ],

    // Ces messages portent sur la DURÉE ÉCOULÉE, jamais sur le travail en cours. « On analyse tes
    // réponses… » prétendrait décrire un état interne qu'on ne connaît pas : le serveur ne renvoie
    // aucun jalon avant sa réponse complète. Aucune de ces phrases ne devient fausse, même si
    // l'appel prend une minute.
    elapsed: {
      start: 'On fabrique ton CV…',
      mid: 'Encore quelques secondes…',
      long: 'C’est un peu plus long que d’habitude, on continue.',
    },
    letter: {
      start: 'On rédige ta lettre…',
      mid: 'Encore quelques secondes…',
      long: 'C’est un peu plus long que d’habitude, on continue.',
    },
    linkedin: {
      start: 'On analyse ton profil…',
      mid: 'Encore quelques secondes…',
      long: 'C’est un peu plus long que d’habitude, on continue.',
    },
    improve: {
      start: 'On optimise ton CV…',
      mid: 'Encore quelques secondes…',
      long: 'C’est un peu plus long que d’habitude, on continue.',
    },
    improveLetter: {
      start: 'On améliore ta lettre…',
      mid: 'Encore quelques secondes…',
      long: 'C’est un peu plus long que d’habitude, on continue.',
    },
    // Jalons RÉELS, cochés seulement quand ils se sont produits.
    received: 'Reçu',
    saved: 'Enregistré',
  },

  // Licence accordée par l'établissement de l'étudiant (lot 4 du back-office). Aucun lien d'achat ici.
  licenceEcole: {
    title: 'Licence école',
    loading: 'Chargement…',
    loadError: 'Impossible de charger ta licence. Réessaie.',
    retry: 'Réessayer',
    intro: 'Ton école t’a envoyé un code d’invitation ? Saisis-le pour activer ta licence.',
    codeLabel: 'Code d’invitation',
    codePlaceholder: 'XXXX-XXXX-XXXX',
    activer: 'Activer ma licence',
    activating: 'Activation…',
    voitTitre: 'Ce que ton école voit',
    voit: [
      'Ta situation (en recherche, en contrat, rupture) et tes dates clés, saisies par ton école.',
      'Les actions d’accompagnement qu’elle a faites pour toi : appels, rendez-vous, offres transmises.',
      'Ton activité dans l’app : les jours où tu l’utilises. Elle est visible dès l’activation de ta licence, car c’est ton école qui te la fournit.',
    ],
    neVoitPas: 'Elle ne voit jamais le contenu de tes CV, de tes lettres ou de tes entretiens.',
    active: 'Licence active',
    via: 'Fournie par %{nom}',
    employeurTitre: 'Ton entreprise',
    employeurContrat: 'Ton école t’a rattaché à %{nom} depuis le %{date}.',
    employeurVoitTitre: 'Ce que ton entreprise verrait si tu l’autorises',
    employeurVoit: [
      'Ton nom, ta filière, ton établissement et les dates de ton contrat.',
      'Ta situation (en recherche, en contrat, rupture).',
    ],
    employeurNeVoitPas: 'Elle ne voit jamais ton e-mail, le journal d’accompagnement de ton école, tes candidatures, ton activité dans l’app, ni le contenu de tes CV, lettres ou entretiens.',
    employeurConsentTitre: 'Partager ma situation avec mon entreprise',
    employeurConsentBody: 'Désactivé par défaut. Tu peux le retirer à tout moment : ton contrat et ta licence ne changent pas. Ton école voit seulement si tu as accepté ou non.',
    employeurConsentError: 'Impossible d’enregistrer ton choix. Réessaie.',
    succes: 'Ta licence %{nom} est activée.',
    erreurs: {
      code_invalide: 'Ce code n’est pas valide. Vérifie-le ou demande-en un nouveau à ton école.',
      invitation_expiree: 'Cette invitation a expiré. Demande-en une nouvelle à ton école.',
      abonnement_actif: 'Tu as déjà un abonnement actif. Résilie-le dans les réglages de l’App Store ou de Google Play, puis réessaie.',
      plus_de_sieges: 'Ton école n’a plus de place disponible. Contacte-la.',
      organisation_inactive: 'La licence de ton école n’est pas active pour le moment. Contacte-la.',
      inconnu: 'Activation impossible pour le moment. Réessaie.',
    },
  },

  settings: {
    title: 'Paramètres',
    sections: {
      account: 'Compte',
      preferences: 'Préférences',
      admin: 'Administration',
      danger: 'Zone de danger',
    },
    privacy: 'Confidentialité et données',
    licenceEcole: 'Licence école',
    dataDeletion: 'Suppression de compte et de données',
    notifications: 'Notifications',
    adminDashboard: 'Dashboard admin',
    signOut: 'Se déconnecter',
    deleteAccount: 'Supprimer mon compte',
    deleting: 'Suppression…',
    deleteFailedTitle: 'Suppression impossible',
    deleteConfirmTitle: 'Supprimer ton compte ?',
    deleteConfirmBody: 'Ton compte sera désactivé tout de suite et tes données supprimées définitivement après 29 jours. Ton abonnement n’est pas résilié automatiquement : résilie-le dans les réglages de l’App Store ou de Google Play.',
    language: {
      label: 'Langue de l’application',
      hint: 'Les documents que tu génères gardent leur propre langue, choisie au moment de les créer.',
      // Arabe ↔ autre langue : le sens d'écriture change, l'application doit redémarrer (i18n/sens.ts).
      relanceTitre: 'Redémarrer l’application ?',
      relanceTexte: 'Pour passer à cette langue, Horméia doit redémarrer. Tes CV, lettres et réglages enregistrés sont conservés ; un entretien ou une saisie en cours non enregistrés seront perdus.',
      relanceOk: 'Redémarrer',
    },
  },
  update: {
    banner: {
      title: 'Une nouvelle version est disponible',
      body: 'Mets à jour Horméia pour profiter des dernières améliorations.',
      action: 'Mettre à jour',
      dismiss: 'Plus tard',
      dismissA11y: 'Masquer cette invitation à mettre à jour',
    },
    /**
     * L'ÉCRAN BLOQUANT DIT POURQUOI, PAS SEULEMENT QUOI. Quelqu'un dont l'application s'arrête sans
     * explication conclut qu'elle est cassée et la désinstalle — un « Mettez à jour » sec ressemble
     * à une injonction commerciale, pas à une raison. D'où l'ordre : le motif, puis la promesse que
     * rien n'est perdu, puis l'action.
     */
    blocking: {
      title: 'Cette version n’est plus compatible',
      body: 'Horméia a évolué depuis l’installation de cette version, qui ne peut plus fonctionner correctement avec le service. Ce n’est pas une panne, et il n’y a rien à réparer de ton côté.',
      reassurance: 'Tes données et tes documents sont intacts : tu les retrouveras dès l’ouverture de la nouvelle version.',
      action: 'Installer la mise à jour',
    },
  },
  // REFUS SANS OFFRE : ce que voit quelqu'un à qui l'on n'a RIEN à vendre au-dessus — abonné Pro,
  // licence école. Le paywall ne s'ouvre pas, et pas un mot ne parle d'abonnement : le lui proposer
  // reviendrait à lui vendre ce qu'il paie déjà.
  quota: {
    // « 1er » et non « 1 » : le premier du mois porte l'ordinal en français.
    resetDate: '1er %{monthYear}',
    capReachedNoUpgrade:
      'Tu as atteint la limite de ton plan pour cette fonctionnalité. Ton compteur repart le %{date}.',
    notIncludedNoUpgrade: 'Cette fonctionnalité n’est pas comprise dans ta licence.',
  },
  errors: {
    // Messages par CODE d'erreur renvoyé par les Edge Functions. Le `detail` du serveur, rédigé en
    // français et décrivant la mécanique interne, n'est plus affiché.
    network: 'Connexion impossible. Vérifie ta connexion, puis réessaie.',
    server: 'Le service est momentanément indisponible. Réessaie dans un instant.',
    aiBadOutput: 'La réponse de l’IA était inexploitable. Relance l’analyse.',
    profilTropEloigne: 'Cette offre est trop éloignée de ton CV pour écrire une lettre crédible.',
    invalidRequest: 'Vérifie les informations saisies, puis réessaie.',
    ficheTropLongue: 'La fiche de poste est trop longue : 20 pages ou 10 000 caractères au plus.',
    unauthorized: 'Ta session a expiré. Reconnecte-toi.',
  },

  // Fenêtre « Nouveautés » montrée UNE fois après une mise à jour (services/nouveautes.ts).
  // Chaque phrase doit rester vraie dans le code et les quotas en vigueur. Noms d'offre : ceux de
  // config/plans.ts et du paywall (Free, Accélérateur, École, Pro), jamais traduits.
  nouveautes: {
    titre: 'Quoi de neuf dans Horméia',
    offresTitre: 'Les offres d’alternance',
    offresTexte: 'Trouve des offres d’alternance et d’emploi qui correspondent à ton profil, puis prépare ta candidature avec l’IA.',
    voirOffres: 'Voir les offres',
    voixTitre: 'La voix du recruteur',
    voixTexte: 'Les simulations d’entretien ont maintenant une voix réaliste. Selon ton offre : 1 entretien par mois en Free, 2 par semaine en Accélérateur ou École, 5 par semaine en Pro.',
    languesTitre: 'Nouvelles langues',
    languesTexte: 'Horméia est maintenant disponible en espagnol, portugais, chinois et arabe. Tu peux changer de langue dans les réglages.',
    politiqueTitre: 'Politique de confidentialité',
    politiqueTexte: 'Notre politique de confidentialité a été mise à jour pour décrire la mesure d’usage de l’application. Tu peux la consulter à tout moment dans les réglages.',
    voirPolitique: 'Lire la politique',
    bouton: 'C’est parti',
  },
} as const;

/**
 * Même ARBORESCENCE que `fr`, valeurs libres. `en` est typé dessus : une clé ajoutée ici sans son
 * équivalent anglais fait échouer tsc, donc la traduction ne peut pas prendre de retard en silence.
 */
type Translatable<T> = {
  [K in keyof T]: T[K] extends string
    ? string
    : T[K] extends { one: string; other: string }
      ? Translatable<T[K]> & FormesPlurielles
      : Translatable<T[K]>;
};

/**
 * Formes de pluriel que le français n'a pas : l'arabe distingue aussi 2 (`two`), 3 à 10 (`few`) et
 * 11 à 99 (`many`), sa règle étant enregistrée dans `i18n/index.ts`. Facultatives : une langue qui
 * ne les porte pas retombe sur `other`.
 */
type FormesPlurielles = { two?: string; few?: string; many?: string };

export type Dictionary = Translatable<typeof fr>;
